import React, { useState, useEffect, useCallback } from 'react';
import { Home, BarChart3, ArrowRightLeft, Users, MoreHorizontal } from 'lucide-react';

const TABS = [
  { id: 'home',       label: 'Home',   icon: Home },
  { id: 'tokenomics', label: 'Stats',  icon: BarChart3 },
  { id: 'buy-trade',  label: 'Buy',    icon: ArrowRightLeft, highlight: true },
  { id: 'community',  label: 'Social', icon: Users },
  { id: 'faq',        label: 'More',   icon: MoreHorizontal },
];

const MobileBottomNav = () => {
  const [active, setActive] = useState('home');
  const [tapped, setTapped] = useState(null);

  /* ── Active-section tracking via IntersectionObserver ──────────────── */
  useEffect(() => {
    const ids = TABS.map(t => t.id);
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible.length > 0) {
          setActive(visible[0].target.id);
        }
      },
      { rootMargin: '-30% 0px -50% 0px', threshold: [0, 0.1, 0.5] }
    );

    ids.forEach(id => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    // Also track scroll to home
    const handleScroll = () => {
      if (window.scrollY < 200) setActive('home');
    };
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const scrollTo = useCallback((id) => {
    setActive(id);
    setTapped(id);
    setTimeout(() => setTapped(null), 300);

    const el = document.getElementById(id);
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - 90;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  }, []);

  return (
    <nav
      aria-label="Mobile navigation"
      className="fixed bottom-0 left-0 right-0 md:hidden"
      style={{
        zIndex: 9999,
        background: 'rgba(7, 7, 16, 0.96)',
        backdropFilter: 'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
        borderTop: '1px solid rgba(153, 69, 255, 0.2)',
        boxShadow: '0 -8px 40px rgba(0,0,0,0.6), 0 -1px 0 rgba(153,69,255,0.12)',
        paddingBottom: 'max(16px, env(safe-area-inset-bottom))',
      }}
    >
      {/* Subtle gradient line at top of nav */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: '10%',
          right: '10%',
          height: '1px',
          background: 'linear-gradient(90deg, transparent, rgba(153,69,255,0.5), rgba(20,241,149,0.3), transparent)',
        }}
      />

      <div className="flex items-end justify-around px-1 pt-3">
        {TABS.map((tab) => {
          const isActive = active === tab.id;
          const isTapped = tapped === tab.id;
          const Icon = tab.icon;

          /* ── Center BUY button (FAB style) ─── */
          if (tab.highlight) {
            return (
              <button
                key={tab.id}
                onClick={() => scrollTo(tab.id)}
                aria-label="Buy / Trade"
                style={{
                  WebkitTapHighlightColor: 'transparent',
                  outline: 'none',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  paddingBottom: '4px',
                  transform: isTapped ? 'scale(0.88)' : 'scale(1)',
                  transition: 'transform 0.15s cubic-bezier(0.34,1.56,0.64,1)',
                }}
              >
                <div
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'linear-gradient(135deg, #9945FF 0%, #14F195 100%)',
                    boxShadow: isTapped
                      ? '0 0 12px rgba(153,69,255,0.3)'
                      : '0 0 28px rgba(153,69,255,0.55), 0 0 50px rgba(20,241,149,0.15), 0 4px 20px rgba(0,0,0,0.5)',
                    marginTop: -20,
                    marginBottom: 3,
                    transition: 'box-shadow 0.2s ease',
                    border: '2px solid rgba(255,255,255,0.15)',
                    position: 'relative',
                  }}
                >
                  <Icon size={22} color="white" />
                </div>
                <span
                  style={{
                    fontSize: 10,
                    color: '#ffffff',
                    fontWeight: 700,
                    letterSpacing: '0.02em',
                    lineHeight: 1,
                  }}
                >
                  {tab.label}
                </span>
              </button>
            );
          }

          /* ── Regular tabs ─── */
          return (
            <button
              key={tab.id}
              onClick={() => scrollTo(tab.id)}
              aria-label={tab.label}
              style={{
                WebkitTapHighlightColor: 'transparent',
                outline: 'none',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                minWidth: 54,
                paddingBottom: 4,
                paddingTop: 4,
                position: 'relative',
                transform: isTapped ? 'scale(0.88)' : 'scale(1)',
                transition: 'transform 0.15s cubic-bezier(0.34,1.56,0.64,1)',
              }}
            >
              {/* Active indicator line */}
              {isActive && (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: '25%',
                    right: '25%',
                    height: 2,
                    borderRadius: 2,
                    background: 'linear-gradient(90deg, #9945FF, #14F195)',
                    boxShadow: '0 0 8px rgba(153,69,255,0.8)',
                  }}
                />
              )}

              {/* Icon container */}
              <div
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 10,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: isActive
                    ? 'linear-gradient(135deg, rgba(153,69,255,0.2) 0%, rgba(20,241,149,0.08) 100%)'
                    : 'transparent',
                  transition: 'background 0.25s ease',
                }}
              >
                <Icon
                  size={20}
                  style={{
                    color: isActive ? '#9945FF' : '#4b5563',
                    filter: isActive ? 'drop-shadow(0 0 6px rgba(153,69,255,0.7))' : 'none',
                    transition: 'color 0.25s ease, filter 0.25s ease',
                  }}
                />
              </div>

              {/* Label */}
              <span
                style={{
                  fontSize: 10,
                  fontWeight: isActive ? 600 : 400,
                  color: isActive ? '#9945FF' : '#4b5563',
                  letterSpacing: '0.01em',
                  lineHeight: 1,
                  transition: 'color 0.25s ease',
                }}
              >
                {tab.label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};

export default MobileBottomNav;
